/* eslint-disable max-lines */
import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener,
  Input, OnDestroy, Output, ViewChild, ViewEncapsulation
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, Observable, iif, Subscription } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { HEADER_TITLE_HEIGHT } from '@app/modules/core/constants/dom.constants';
import { AirlineNamePipe } from '@app/pipes/airline-name.pipe';
import { StopNamePipe } from '@app/pipes/stop-name.pipe';
import { PackageFilterProvider } from '@packages/packages-result/constants/package-filter-provider';
import { HotelFilterApplier, HotelFilterInputTypes, HotelFilterTypes } from '@shared/hotel-itinerary/hotel-filters';
import {
  CheckboxFilter,
  CheckboxFilterVm,
  FilterSorter,
  FilterSorterCriterias,
  PickedRange,
  PickedRangeValues,
  PriceFilter,
  PriceRangeFilter,
  RangeType
} from '@shared/filters/entities/fetch-filters.response';
import { PackagesResultFiltersFacade } from '@packages/packages-result/store/package-result-filters.facade';
import {
  HOTEL_NON_REFUNDABLE_TITLES, NO_ZONE_LABEL, STARS_LABEL, STAR_LABEL
} from '@hotels/hotel-results/constants/filters';
import { getHotelItemLabel } from '@shared/filters/mappers/hotel-filters-label.mapper';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import {
  pickedPackageHotelCheckboxes, pickedPackageHotelRanges, pickedPackageHotelSorter, pickedPackageHotelStars
} from '@packages/packages-result/mappers/hotel-package-filters.mapper';
import { FilterApplier, FilterTypes, StopTypes } from '@shared/flight-itinerary/entities/filters';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import {
  pickedFlightPackageCheckboxes, pickedPackageFlightRanges, pickedPackageFlightAirportCheckboxes
} from '@packages/packages-result/mappers/flight-package-filters.mapper';
import { TripTypes } from '@app/entities/trip-types';
import { FILTER_TITLES } from '@flight-results/constants/filters.constants';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { HotelFilterBarService } from '@shared/magic-filter-bar/hotel-filter-bar/services/hotel-filter-bar.service';
import { SharedSelectedFilterAirValue, SharedSelectedFilterHotelValue } from '@shared/magic-filter-bar/entities/shared-props';
import { evalHasFilterByExcludeCollection } from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import { evalHasAirFilterByExcludeCollection } from '@shared/magic-filter-bar/air-filter-bar/mappers/air-filters-in-bar.mapper';
import { AirFilterVm, HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import {
  isCheckBoxByAirFilter, isCheckRangeByAirFilter, isSectionCheckBoxByAirFilter
} from '@app/mappers/global-filters/air-global-filters.mapper';
import { hotelFilterIsNoZoneLabel, hotelFilterNeedsTranslation } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { getDestinationByV2 } from '@packages/packages-search-bar/mappers/packages-url.mapper';

@Component({
  selector: 'app-selected-package-filters',
  templateUrl: './selected-package-filters.container.html',
  styleUrls: ['./selected-package-filters.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class SelectedPackageFiltersContainer implements AfterViewInit, OnDestroy {

  @Input() provider: PackageFilterProvider = PackageFilterProvider.AIR;
  @Input() codePlaces: { departure: string, arrival: string };
  @Input() isMobile: boolean;
  @Input() inFilterBar: boolean;

  @Output() sharedShouldShowFilters = new EventEmitter<boolean>();
  @Output() sharedSelectedHotelFilters = new EventEmitter<{ [key: string]: SharedSelectedFilterHotelValue }>();
  @Output() sharedSelectedAirFilters = new EventEmitter<{ [key: string]: SharedSelectedFilterAirValue }>();
  @Output() checkboxEvent: EventEmitter<{
    checked: boolean,
    isMultiple: boolean,
    isCheckbox: boolean,
    filter: HotelFilterVm
  }> = new EventEmitter<{
    checked: boolean,
    isMultiple: boolean,
    isCheckbox: boolean,
    filter: HotelFilterVm
  }>();

  @ViewChild('stickyRef') stickyRef: ElementRef;

  public sticky = false;

  private readonly selectedStarItems: CheckboxFilter[] = [];

  private readonly subscriptions: Subscription = new Subscription();

  @HostListener('window:scroll', []) handleScroll(): void {
    this.sticky = this.isMobile && this.stickyRef?.nativeElement?.getBoundingClientRect().top <= HEADER_TITLE_HEIGHT;
  }

  constructor(
    private readonly translateService: TranslocoService,
    private readonly airlineName: AirlineNamePipe,
    private readonly stopName: StopNamePipe,
    private readonly facade: PackagesResultFiltersFacade,
    private readonly resultsFacade: PackagesResultFacade,
    private readonly domManipulateService: DomManipulateService,
    private readonly hotelFilterBarService: HotelFilterBarService,
  ) { }

  ngAfterViewInit(): void {
    if (this.inFilterBar) {
      if (this.provider === PackageFilterProvider.HOTEL) {
        this.subscriptions.add(
          combineLatest([
            this.pickedPackageHotelCheckboxes$.pipe(distinctUntilChanged()),
            this.pickedPackageHotelRanges$.pipe(distinctUntilChanged()),
            this.memberRatesOnly$.pipe(distinctUntilChanged()),
            this.pickedPackageHotelStars$.pipe(distinctUntilChanged()),
            this.pickedPackageHotelTotalDiscountPercentage$.pipe(distinctUntilChanged()),
            this.hotelNameSearch$.pipe(distinctUntilChanged()),
            this.shouldShowFilters$.pipe(distinctUntilChanged()),
          ]).pipe(
            tap(([
              pickedHotelCheckboxes, pickedHotelRanges, memberRatesOnly,
              pickedHotelStars, pickedHotelTotalDiscountPercentage, hotelNameSearch, shouldShowFilters
            ]: [string[], PickedRange, boolean, string[], number, string, boolean]) => {
              this.sharedShouldShowFilters.emit(shouldShowFilters);
              this.sharedSelectedHotelFilters.emit({
                hotelNameSearch,
                selectedStarItems: this.selectedStarItems,
                pickedCheckboxes: pickedHotelCheckboxes,
                pickedRanges: pickedHotelRanges,
                pickedMemberRatesOnly: memberRatesOnly,
                pickedStars: pickedHotelStars,
                pickedTotalDiscountPercentage: pickedHotelTotalDiscountPercentage,
              });
            })
          ).subscribe()
        );
      } else {
        this.subscriptions.add(
          combineLatest([
            this.pickedPackageAirCheckboxes$.pipe(distinctUntilChanged()),
            this.pickedPackageAirRange$.pipe(distinctUntilChanged()),
            this.pickedPackageAirportsCheckboxes$.pipe(distinctUntilChanged()),
            this.shouldShowFilters$.pipe(distinctUntilChanged()),
          ]).pipe(
            tap(([
              checkboxes, ranges, airportCheckboxes, shouldShowFilters
            ]) => {
              this.sharedShouldShowFilters.emit(shouldShowFilters);
              this.sharedSelectedAirFilters.emit({
                pickedCheckboxes: checkboxes,
                pickedRanges: ranges,
                pickedAirportCheckboxes: airportCheckboxes
              });
            })
          ).subscribe()
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get classProvider(): string {
    return this.inFilterBar ? `in-filter-bar-${this.provider?.toLowerCase()}` : '';
  }

  get hotelFilterTypes(): typeof HotelFilterTypes {
    return HotelFilterTypes;
  }

  private get pickedPackageHotelRanges$(): Observable<PickedRange> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelRanges(payload)));
  }

  private get pickedPackageHotelStars$(): Observable<string[]> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelStars(payload)));
  }

  private get pickedPackageHotelCheckboxes$(): Observable<string[]> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelCheckboxes(payload)));
  }

  private get pickedPackageHotelSorter$(): Observable<FilterSorterCriterias> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => pickedPackageHotelSorter(payload)));
  }

  private get pickedPackageHotelTotalDiscountPercentage$(): Observable<number> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => payload.TotalDiscountPercentageFrom));
  }

  private get pickedPackageAirCheckboxes$(): Observable<string[]> {
    return this.resultsFacade.flightsPayload$.pipe(map(payload => pickedFlightPackageCheckboxes(payload)));
  }

  private get pickedPackageAirRange$(): Observable<PickedRange> {
    return this.resultsFacade.flightsPayload$.pipe(map(payload => pickedPackageFlightRanges(payload)));
  }

  private get pickedPackageAirportsCheckboxes$(): Observable<AirportsFilter> {
    return this.resultsFacade.flightsPayload$.pipe(map(payload => pickedPackageFlightAirportCheckboxes(payload)));
  }

  private get sortingHasBeenChanged$(): Observable<boolean> {
    return this.hotelFilterBarService.sortingHasBeenChangedByHotel$;
  }

  get hotelFilters$(): Observable<HotelFilterVm[]> {
    return this.facade.packageHotelFilters$;
  }

  get airFilters$(): Observable<AirFilterVm[]> {
    return this.facade.packageAirFilters$;
  }

  get hotelNameSearch$(): Observable<string> {
    return this.resultsFacade.hotelsPayload$.pipe(map(payload => payload.HotelName));
  }

  get hasHotelNameSearch$(): Observable<boolean> {
    return this.hotelNameSearch$.pipe(map(hotelNameSearch => !!hotelNameSearch));
  }

  get memberRatesOnly$(): Observable<boolean> {
    return this.resultsFacade.hotelsPayload$.pipe(map(hotelsPayload => hotelsPayload?.MemberRatesOnly))
  }

  public totalDiscountPercentageText$(filter: HotelFilterVm): Observable<string> {
    return this.pickedPackageHotelTotalDiscountPercentage$.pipe(map(discount => {
      const valueOfFilter = [...filter.value as PriceRangeFilter[]].find(item => item.LowerBound === discount);
      return valueOfFilter?.Text;
    }));
  }

  get shouldShowFilters$(): Observable<boolean> {
    return iif(
      () => this.provider === PackageFilterProvider.HOTEL,
      combineLatest([
        this.pickedPackageHotelCheckboxes$, 
        this.pickedPackageHotelStars$,
        this.pickedPackageHotelRanges$,
        this.hasHotelNameSearch$,
        this.pickedPackageHotelTotalDiscountPercentage$,
        this.pickedPackageHotelSorter$,
        this.sortingHasBeenChanged$
      ]).pipe(
        map(([checkbox, stars, ranges, hasHotelName, totalDiscountPercentage, sorter, sortingHasBeenChanged]) => {
          return  !isEmpty(checkbox) || !isEmpty(stars) || !isEmpty(ranges) || !!hasHotelName || 
            (totalDiscountPercentage > 0) || (!!sorter && sortingHasBeenChanged);
        })
      ),
      combineLatest([
        this.pickedPackageAirCheckboxes$, this.pickedPackageAirportsCheckboxes$, this.pickedPackageAirRange$
      ]).pipe(
        map(([checkbox, airports, ranges]) => {
          return !!checkbox?.length || this.checkEmptyRanges(ranges) || this.checkEmptyAirportCheckboxes(airports);
        }))
    );
  }

  get packageFilterProvider(): typeof PackageFilterProvider {
    return PackageFilterProvider;
  }

  public checkedHotelCheckbox$(item: CheckboxFilterVm, type: string, isCheckboxFilter: boolean): Observable<boolean> {
    return combineLatest([this.pickedPackageHotelCheckboxes$, this.pickedPackageHotelStars$]).pipe(
      map(([pickedCheckboxes, pickedStars]) => {
        const validate = this.hotelFilterBarService.checkedHotelCheckbox(
          pickedCheckboxes, pickedStars, this.selectedStarItems, item, isCheckboxFilter
        );
        if (validate.addStars) {
          this.selectedStarItems.push(item);
        }
        return evalHasFilterByExcludeCollection(type, this.inFilterBar, true, validate.include, item.Text);
      }));
  }

  public checkedHotelSorter$(items: HotelFilterVm): Observable<FilterSorter> {
    return combineLatest([this.sortingHasBeenChanged$, this.pickedPackageHotelSorter$]).pipe(
      map(([sortingHasBeenChanged, sorter]) => {
        return sortingHasBeenChanged && (items.value as FilterSorter[])
          .find(item => item.OrderCriteria === sorter.OrderCriteria && item.OrderDesc === sorter.OrderDesc);
      })
    )
  }

  public checkedHotelRanges$(type: string): Observable<PickedRangeValues> {
    return this.pickedPackageHotelRanges$.pipe(map(pickedRanges =>
      this.hotelFilterBarService.checkedHotelRanges(pickedRanges, type)));
  }

  public checkedAirport$(airportCode: string, type: FilterTypes): Observable<boolean> {
    return this.pickedPackageAirportsCheckboxes$.pipe(map(pickedAirportCheckboxes => {
      if (type === FilterTypes.ArrivalAirports) {
        return pickedAirportCheckboxes.ArrivalAirports.reduce((arr, item) =>
          [...arr, ...item.Airports], []).includes(airportCode);
      } else {
        return pickedAirportCheckboxes.DepartureAirports
          .reduce((arr, item) => [...arr, ...item.Airports], []).includes(airportCode);
      }
    }));
  }

  public checkedAirRanges$(type: string): Observable<PickedRangeValues> {
    return this.pickedPackageAirRange$.pipe(map(pickedRanges => {
      if (type === FilterTypes.PriceRange) {
        return pickedRanges && !isEmpty(pickedRanges[type]) ? pickedRanges[type] : null;
      } else {
        return null;
      }
    }));
  }

  public airComplexRangeIsChecked$(type: string): Observable<boolean> {
    return this.checkedAirComplexRanges$(type).pipe(map(checkedAirRanges => {
      const complexRangeHasBeenSelected = !isEmpty(checkedAirRanges);
      return evalHasAirFilterByExcludeCollection(type, this.inFilterBar, true, complexRangeHasBeenSelected, null);
    }));
  }

  public airRangeIsChecked$(type: string): Observable<boolean> {
    return this.checkedAirRanges$(type).pipe(map(checkedAirRanges => {
      const rangeHasBeenSelected = !isEmpty(checkedAirRanges);
      return evalHasAirFilterByExcludeCollection(type, this.inFilterBar, true, rangeHasBeenSelected, null);
    }));
  }

  public checkedAirComplexRanges$(type: string): Observable<PickedRangeValues> {
    return this.pickedPackageAirRange$.pipe(map(pickedRanges => {
      switch (type) {
        case FilterTypes.DepartureTimes:
        case FilterTypes.SectorDurations:
          return pickedRanges?.[type] ?
            Object.keys(pickedRanges[type]).map(obj => !isEmpty(pickedRanges[type][obj]) && pickedRanges[type][obj]) : null;
        default:
          return null;
      }
    }));
  }

  public checkedAirCheckbox$(item: CheckboxFilterVm, type: string, isCheckboxFilter: boolean): Observable<boolean> {
    return this.pickedPackageAirCheckboxes$.pipe(
      map(pickedCheckboxes => {
        const validate = isCheckboxFilter ?
          this.isMobile ? item.Text !== StopTypes.Stops0 && pickedCheckboxes.includes(item.Text) :
            pickedCheckboxes.includes(item.Text) : false;
        return evalHasAirFilterByExcludeCollection(type, this.inFilterBar, true, validate, item.Text);
      })
    );
  }

  public withTranslationsInAirFilters(type: string): boolean {
    return type as FilterTypes === FilterTypes.Airlines || type as FilterTypes === FilterTypes.Stops;
  }

  public getAirCheckboxLabelTranslations(item: CheckboxFilter, type: string): string {
    return this.labelTranslationInAirFilter(item, type);
  }

  public getAirCheckboxLabel(item: CheckboxFilter): string | number {
    return item.TextTransformed ? item.TextTransformed : item.Text;
  }

  public getRangeType(type: string): string {
    return type === FilterTypes.PriceRange ? RangeType.PRICE : RangeType.TIME;
  }
  public getAirItemLabel(label: string): string {
    return FILTER_TITLES[label];
  }

  public getSectorFilterLabel(type: FilterTypes, sector: number, airportName: string): string {
    return `${this.translateService.translate(this.getAirItemLabel(type), {
      filterCriteria: this.getCriteria(type, sector)
    })} - ${airportName}`
  }

  public getCriteria(type: FilterTypes, sector: number): string {
    const { arrival, departure } = this.codePlaces;
    const destinationByV2 = getDestinationByV2(arrival);
    const destination = destinationByV2.Code;
    const departureToArrivalText = `${departure} > ${destination}`;
    if (type === FilterTypes.SectorDurations || (type === FilterTypes.DepartureTimes && sector === 0)) {
      return departureToArrivalText;
    } else if (type === FilterTypes.DepartureTimes) {
      return sector === 0 ? `${departure} > ${destination}` : `${destination} > ${departure}`;
    } else if (sector === TripTypes.DEPARTURE) {
      return departureToArrivalText;
    } else {
      return `${destination} > ${departure}`;
    }
  }

  public isCheckboxHotelFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Checkbox && (item.value as CheckboxFilter[]).length > 0;
  }

  public isRadioHotelFilter(filter: HotelFilterVm): boolean {
    const type = filter.type;
    const isRadio = filter.filter === HotelFilterInputTypes.Radio;
    if (type === HotelFilterTypes.HotelPriceRanges) {
      return isRadio && (filter.value as PriceRangeFilter[]).length > 0;
    } else if (type === HotelFilterTypes.PromotionalRates) {
      return isRadio && (filter.value as PriceRangeFilter[]).length > 0;
    } else {
      return false;
    }
  }

  public checkedRadioHotelFilter$(item: HotelFilterVm): Observable<boolean> {
    return this.totalDiscountPercentageText$(item).pipe(map(discountText => {
      return evalHasFilterByExcludeCollection(item.type, this.inFilterBar, false, !!discountText, discountText);
    }));
  }

  public isHotelCheckboxFilterTypes(item: HotelFilterVm): boolean {
    return this.isCheckboxHotelFilter(item) || this.isHotelStarsFilter(item);
  }

  public isHotelSorter(item: HotelFilterVm): boolean {
    return item.type === HotelFilterTypes.Sorters;
  }

  public isRangeHotelFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Range &&
      (item.value as PriceFilter).min !== (item.value as PriceFilter).max;
  }

  public isCheckboxAirFilter(item: AirFilterVm): boolean {
    return isCheckBoxByAirFilter(item.type);
  }
  public isSectionCheckBoxAirFilter(item: AirFilterVm): boolean {
    return isSectionCheckBoxByAirFilter(item.type);
  }
  public isRangeAirFilter(item: AirFilterVm): boolean {
    return isCheckRangeByAirFilter(item.type);
  }
  public getHotelRangeItemLabel(label: string): string {
    return getHotelItemLabel(label);
  }

  public getHotelCheckboxLabel(item: CheckboxFilter, type: string): string | number {
    return hotelFilterNeedsTranslation(type) ? this.labelTranslation(item, type) :
      item.TextTransformed ? item.TextTransformed :
        hotelFilterIsNoZoneLabel(item) ? this.translateService.translate(NO_ZONE_LABEL) : item.Text;
  }

  public removeHotelCheckboxFilter(item: CheckboxFilter, type: string, isCheckboxFilter: boolean): void {
    if (isCheckboxFilter) {
      this.applyHotelFilterVm({ type: type as HotelFilterTypes, value: item.Text, removable: true });
    } else {
      this.applyHotelFilterVm({
        type: type as HotelFilterTypes, value: this.selectedStarItems.filter(_item => _item.Text !== item.Text)
      });
    }
  }

  public removeAirportCheckboxFilter(value: string, sector: number, type: FilterTypes): void {
    this.applyAirFilterVm({ sector, type, value, removable: true });
  }

  public removeAirRangeFilter(type: string): void {
    this.applyAirFilterVm({ type: type as FilterTypes, value: [] });
  }

  public removeAirComplexRangeFilter(type: string, sector: number): void {
    this.applyAirFilterVm({ type: type as FilterTypes, sector: sector, value: [] });
  }

  public removeHotelRangeFilter(type: string): void {
    this.applyHotelFilterVm({ type: type as HotelFilterTypes, value: [] });
  }

  public removeAirCheckboxFilter(item: CheckboxFilter, type: string, isCheckboxFilter: boolean): void {
    if (isCheckboxFilter) {
      this.applyAirFilterVm({ type: type as FilterTypes, value: item.Text, removable: true });
    }
  }

  public removeHotelNameFilter(): void {
    this.applyHotelFilterVm({ type: HotelFilterTypes.HotelName, value: '' });
  }

  public removeDiscountFilter(): void {
    this.applyHotelFilterVm({ type: HotelFilterTypes.PromotionalRates, value: 0 });
  }

  public trackById(index: number): number {
    return index;
  }

  public toggleSorterSelection(filter: HotelFilterVm): void {
    this.checkboxEvent.emit({
      filter: filter,
      checked: true,
      isMultiple: true,
      isCheckbox: false
    });
  }

  private applyHotelFilterVm(_filter: HotelFilterApplier): void {
    this.domManipulateService.resetScrollPosition();
    this.facade.fetchHotelsWithFilters(_filter, true, false);
  }

  private applyAirFilterVm(_filter: FilterApplier): void {
    this.domManipulateService.resetScrollPosition();
    this.resultsFacade.fetchAirWithFilter(_filter, this.isMobile);
  }

  private labelTranslation(item: CheckboxFilter, type: string): string {
    return type === HotelFilterTypes.NonRefundable ? this.translateService.translate(HOTEL_NON_REFUNDABLE_TITLES[item.Text]) :
      type === HotelFilterTypes.Category ? `${item.Text} ${this.translateService.translate(this.getCategoryLabel(item.Text))}` :
        item.Text;
  }

  private isHotelStarsFilter(item: HotelFilterVm): boolean {
    return item.filter === HotelFilterInputTypes.Stars && (item.value as CheckboxFilter[]).length > 0;
  }

  private checkEmptyRanges(ranges: PickedRange): boolean {
    return Object.keys(ranges).some(key => Object.keys(ranges[key]).some((k) => !isEmpty(ranges[key][k])));
  }

  private checkEmptyAirportCheckboxes(airports: AirportsFilter): boolean {
    return Object.keys(airports).some(key => !isEmpty(airports[key]));
  }

  private labelTranslationInAirFilter(item: CheckboxFilter, type: string): string {
    if (type === FilterTypes.Airlines) {
      return this.airlineName.transform(item.Label);
    } else if (type === FilterTypes.Stops) {
      return this.translateService.translate(this.stopName.transform(item.Text));
    } else {
      return '';
    }
  }

  private getCategoryLabel(label: string): string {
    return parseInt(label) === 1 ? STAR_LABEL : STARS_LABEL;
  }
}
